import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Building2, 
  User, 
  Mail, 
  Phone, 
  Globe, 
  FileText, 
  Lock, 
  CheckCircle,
  Loader2,
  ArrowRight,
  Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function SponsorSignupPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState(1);
  
  const [form, setForm] = useState({
    token: searchParams.get('token') || '',
    company_name: '',
    contact_name: '',
    email: searchParams.get('email') || '',
    phone: '',
    website: '',
    description: '',
    why_sponsor: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    if (!searchParams.get('token')) {
      toast.error('Invalid invitation link. Please contact admin for a new invitation.');
    }
  }, [searchParams]);

  const handleSubmit = async () => {
    // Validation
    if (!form.company_name.trim()) {
      toast.error('Please enter your company name');
      return;
    }
    if (!form.contact_name.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!form.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!form.description.trim()) {
      toast.error('Please describe your company');
      return;
    }
    if (!form.why_sponsor.trim()) {
      toast.error('Please tell us why you want to sponsor');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/sponsor/signup`, {
        token: form.token,
        company_name: form.company_name,
        contact_name: form.contact_name,
        email: form.email,
        phone: form.phone,
        website: form.website,
        description: form.description,
        why_sponsor: form.why_sponsor,
        password: form.password
      });
      
      setSuccess(true);
      toast.success('Application submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-200 shadow-xl">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-amber-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#1E3A5F] mb-2">Application Submitted!</h2>
            <p className="text-slate-600 mb-4">
              Thank you for applying to become a DealLab CE sponsor. Your application is now <strong>pending approval</strong>.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-amber-800">
                You will receive an email notification once your application has been reviewed. 
                This typically takes 1-2 business days.
              </p>
            </div>
            <Link to="/public">
              <Button variant="outline" className="mt-2">
                Return to Home
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 to-emerald-50">
      {/* Header */}
      <div className="bg-[#1E3A5F] text-white py-8">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Become a DealLab CE Sponsor
          </h1>
          <p className="text-slate-300">
            Connect with motivated Texas real estate professionals
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className={`flex items-center ${step >= 1 ? 'text-emerald-600' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>
              1
            </div>
            <span className="ml-2 font-medium">Company Info</span>
          </div>
          <div className={`w-16 h-0.5 mx-4 ${step >= 2 ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
          <div className={`flex items-center ${step >= 2 ? 'text-emerald-600' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>
              2
            </div>
            <span className="ml-2 font-medium">About You</span>
          </div>
          <div className={`w-16 h-0.5 mx-4 ${step >= 3 ? 'bg-emerald-500' : 'bg-slate-200'}`}></div>
          <div className={`flex items-center ${step >= 3 ? 'text-emerald-600' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-emerald-500 text-white' : 'bg-slate-200'}`}>
              3
            </div>
            <span className="ml-2 font-medium">Account</span>
          </div>
        </div>

        <Card className="border-slate-200 shadow-lg">
          <CardContent className="p-6">
            {/* Step 1: Company Info */}
            {step === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <Building2 className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                  <h2 className="text-xl font-semibold text-[#1E3A5F]">Company Information</h2>
                  <p className="text-slate-500 text-sm">Tell us about your organization</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Company Name *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Your Company Name"
                        value={form.company_name}
                        onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                        className="pl-10"
                        data-testid="sponsor-company-name"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Contact Name *</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Your Full Name"
                        value={form.contact_name}
                        onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                        className="pl-10"
                        data-testid="sponsor-contact-name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="email"
                          value={form.email}
                          disabled
                          className="pl-10 bg-slate-50"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 mb-1 block">Phone *</label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                          type="tel"
                          placeholder="(555) 555-5555"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          className="pl-10"
                          data-testid="sponsor-phone"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Website (optional)</label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="url"
                        placeholder="https://www.yourcompany.com"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        className="pl-10"
                        data-testid="sponsor-website"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4">
                  <Button 
                    onClick={() => setStep(2)}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    disabled={!form.company_name || !form.contact_name || !form.phone}
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: About You */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <FileText className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                  <h2 className="text-xl font-semibold text-[#1E3A5F]">Tell Us More</h2>
                  <p className="text-slate-500 text-sm">Help us understand your business and goals</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">About Your Company *</label>
                    <Textarea
                      placeholder="Describe your company, products/services, and how you serve real estate professionals..."
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      className="h-32"
                      data-testid="sponsor-description"
                    />
                    <p className="text-xs text-slate-400 mt-1">This will be visible on your sponsor profile</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Why Do You Want to Sponsor? *</label>
                    <Textarea
                      placeholder="What are your sponsorship goals? What type of exposure are you looking for?"
                      value={form.why_sponsor}
                      onChange={(e) => setForm({ ...form, why_sponsor: e.target.value })}
                      className="h-32"
                      data-testid="sponsor-why"
                    />
                    <p className="text-xs text-slate-400 mt-1">This helps us match you with the right opportunities</p>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    Back
                  </Button>
                  <Button 
                    onClick={() => setStep(3)}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    disabled={!form.description || !form.why_sponsor}
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Create Account */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <Lock className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
                  <h2 className="text-xl font-semibold text-[#1E3A5F]">Create Your Account</h2>
                  <p className="text-slate-500 text-sm">Set up your login credentials</p>
                </div>

                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <Sparkles className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-emerald-800">
                      <p className="font-medium">Once approved, you'll be able to:</p>
                      <ul className="list-disc ml-4 mt-1">
                        <li>View and sponsor upcoming CE courses</li>
                        <li>Upload your company logo</li>
                        <li>Track your sponsorship commitments</li>
                        <li>Connect with course registrants</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="password"
                        placeholder="Create a password (min 6 characters)"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                        className="pl-10"
                        data-testid="sponsor-password"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Confirm Password *</label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="password"
                        placeholder="Confirm your password"
                        value={form.confirmPassword}
                        onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                        className="pl-10"
                        data-testid="sponsor-confirm-password"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    Back
                  </Button>
                  <Button 
                    onClick={handleSubmit}
                    className="bg-emerald-500 hover:bg-emerald-600"
                    disabled={loading || form.password.length < 6 || form.password !== form.confirmPassword}
                    data-testid="sponsor-submit"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Submit Application
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Benefits */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4 border border-slate-200 text-center">
            <div className="w-10 h-10 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <User className="w-5 h-5 text-sky-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Direct Access</h3>
            <p className="text-sm text-slate-500">Connect with motivated agents</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200 text-center">
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Building2 className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Brand Visibility</h3>
            <p className="text-sm text-slate-500">Your logo on all materials</p>
          </div>
          <div className="bg-white rounded-lg p-4 border border-slate-200 text-center">
            <div className="w-10 h-10 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-2">
              <Sparkles className="w-5 h-5 text-violet-600" />
            </div>
            <h3 className="font-semibold text-slate-800">Lead Generation</h3>
            <p className="text-sm text-slate-500">Grow your business</p>
          </div>
        </div>
      </div>
    </div>
  );
}
